import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { gql } from '../lib/api';
import {
  BENCHMARK_QUERY,
  COST_ENTRIES_QUERY,
  FIXED_COST_ITEMS_QUERY,
  LABOR_COST_ITEMS_QUERY,
  LATEST_TXN_DATES_QUERY,
  LOANS_QUERY,
  MONTHLY_SUMMARY_QUERY,
  SHIFTS_QUERY,
  STAFF_QUERY,
  STORES_DETAIL_QUERY,
  TRANSACTIONS_QUERY,
  WEEKLY_SUMMARY_QUERY,
} from '../graphql/queries';
import { PaymentDonut } from '../components/charts/PaymentDonut';
import { CategoryDonut } from '../components/charts/CategoryDonut';
import {
  computeLaborCostForDateRange,
  computeLaborCostForMonth,
  type ShiftLite,
  type StaffLite,
} from '../lib/laborCost';
import {
  calendarYearRange,
  dateStrToIsoWeek,
  effectiveDayCount,
  fiscalYearRange,
  formatRangeLabel,
  monthDateRange as periodMonthRange,
  monthlyDaysInRange,
  monthsInRange,
  todayStr,
  type DateRange,
} from '../lib/periodCalc';
import { airRegiBusinessDate, airRegiService } from '../lib/airRegiBusinessTime';

// ============================================================================
// 型定義
// ============================================================================
interface Store {
  id: string;
  name: string;
  airregiBusinessDayStart: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  dinnerStart: string | null;
  dinnerEnd: string | null;
}

interface PeriodSummary {
  storeId: string;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  sales: number;
  txnCount: number;
  customerCount: number;
  partyCount: number;
  avgSpend: number | null;
  avgPartySize: number | null;
  avgItemsPerTxn: number | null;
  avgStayDurationSec: number | null;
  lunchSales: number | null;
  lunchTxnCount: number | null;
  lunchCustomerCount: number | null;
  dinnerSales: number | null;
  dinnerTxnCount: number | null;
  dinnerCustomerCount: number | null;
  otherSales: number | null;
  otherTxnCount: number | null;
  otherCustomerCount: number | null;
  foodOrderQuantity: number | null;
  drinkOrderQuantity: number | null;
  byPayment: { method: string; amount: number }[];
  byCategory: { category: string; sales: number; quantity: number }[];
  byReceipt: { receiptName: string; sales: number; txnCount: number }[];
  hourlyDistribution: { hour: number; txnCount: number; sales: number }[];
  topProducts: { menuName: string; quantity: number; sales: number }[];
}

interface CostEntry {
  storeId: string;
  date: string;
  category: string;
  itemName: string;
  reportInput: string | null;
  amount: number;
}

interface FixedCostItem {
  storeId: string;
  itemKey: string;
  itemName: string;
  amount: number;
}

interface LaborCostItem {
  storeId: string;
  itemKey: string;
  itemName: string;
  amount: number;
}

interface Staff extends StaffLite {
  displayName: string;
}

interface Shift extends ShiftLite {
  displayName: string;
}

interface TxnItem {
  category: string | null;
  menuName: string;
  unitPrice: number | null;
  quantity: number;
}

interface Transaction {
  storeId: string;
  txnNo: string;
  visitDate: string | null;
  visitTime: string | null;
  accountDate: string;
  accountTime: string;
  total: number;
  customerCount: number | null;
  itemCount: number | null;
  items: TxnItem[];
}

interface Loan {
  loanId: string;
  name: string;
  outstanding: number;
  monthlyPayment: number;
}

interface Benchmark {
  foodCostPct: number;
  laborCostPct: number;
  flPct: number;
  rentPct: number;
  otherPct: number;
  profitPct: number;
}

type Period = 'year' | 'month' | 'week' | 'custom';
type YearMode = 'rolling12' | 'fiscal' | 'calendar';
type LDFilter = 'all' | 'lunch' | 'dinner';

const FISCAL_START_MONTH = 8; // 法人決算月: 7 月末 → 年度は 8 月開始
const todayDate = new Date();
const currentYM = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}`;
const currentDateStr = todayStr();
const currentFiscalYear =
  todayDate.getMonth() + 1 >= FISCAL_START_MONTH
    ? todayDate.getFullYear()
    : todayDate.getFullYear() - 1;

// ============================================================================
// ユーティリティ
// ============================================================================
const monthsBack = (n: number, baseYearMonth: string): string[] => {
  const [y, m] = baseYearMonth.split('-').map(Number);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(y, m - 1 - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

const isoWeekRange = (isoWeek: string): { from: string; to: string } => {
  const [yStr, wStr] = isoWeek.split('-W');
  const year = Number(yStr);
  const week = Number(wStr);
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = new Date(simple);
  if (dayOfWeek <= 4) isoWeekStart.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  else isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  const end = new Date(isoWeekStart);
  end.setUTCDate(end.getUTCDate() + 6);
  return {
    from: isoWeekStart.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
};

const toIsoWeek = (date: Date): string => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      ((target.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7,
    );
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

const weeksBack = (n: number, baseIsoWeek: string): string[] => {
  const { from } = isoWeekRange(baseIsoWeek);
  const baseDate = new Date(`${from}T00:00:00Z`);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(baseDate);
    d.setUTCDate(d.getUTCDate() - 7 * i);
    out.push(toIsoWeek(d));
  }
  return out;
};

const fmtYen = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n) ? `¥${Math.round(n).toLocaleString()}` : '—';

const fmtPct = (n: number | null | undefined, digits = 1): string =>
  typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(digits)}%` : '—';

const nextDate = (date: string): string => {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
};

// ============================================================================
// データ集約: PeriodSummary を期間（year/month/week）× LD で抽出
// ============================================================================
const sumSummaries = (
  summaries: PeriodSummary[],
  ld: LDFilter,
): { sales: number; partyCount: number; customerCount: number; foodQty: number; drinkQty: number } => {
  let sales = 0;
  let partyCount = 0;
  let customerCount = 0;
  let foodQty = 0;
  let drinkQty = 0;
  for (const s of summaries) {
    if (ld === 'lunch') {
      sales += s.lunchSales ?? 0;
      partyCount += s.lunchTxnCount ?? 0;
      customerCount += s.lunchCustomerCount ?? 0;
    } else if (ld === 'dinner') {
      sales += s.dinnerSales ?? 0;
      partyCount += s.dinnerTxnCount ?? 0;
      customerCount += s.dinnerCustomerCount ?? 0;
    } else {
      sales += s.sales ?? 0;
      partyCount += s.partyCount ?? 0;
      customerCount += s.customerCount ?? 0;
    }
    foodQty += s.foodOrderQuantity ?? 0;
    drinkQty += s.drinkOrderQuantity ?? 0;
  }
  return { sales, partyCount, customerCount, foodQty, drinkQty };
};

// ============================================================================
// メインコンポーネント
// ============================================================================
export const DashboardPage = ({ isAdmin }: { isAdmin: boolean }) => {
  // URL クエリパラメータと同期する state
  // URL を共有すると同じ画面が再現できる
  const [searchParams, setSearchParams] = useSearchParams();

  // 初期値を URL から取得（無効値はデフォルトに fallback）
  const initStore = searchParams.get('store') ?? 'all';
  const periodParam = searchParams.get('period');
  const initPeriod: Period =
    periodParam === 'week' || periodParam === 'month' || periodParam === 'year' || periodParam === 'custom'
      ? periodParam
      : 'week';
  const ldParam = searchParams.get('ld');
  const initLd: LDFilter =
    ldParam === 'all' || ldParam === 'lunch' || ldParam === 'dinner' ? ldParam : 'all';
  const ymodeParam = searchParams.get('ymode');
  const initYearMode: YearMode =
    ymodeParam === 'rolling12' || ymodeParam === 'calendar' || ymodeParam === 'fiscal'
      ? ymodeParam
      : 'rolling12';
  const ymParam = searchParams.get('ym');
  const initYearMonth = /^\d{4}-\d{2}$/.test(ymParam ?? '') ? (ymParam as string) : currentYM;
  const wdParam = searchParams.get('wd');
  const initWeekDate = /^\d{4}-\d{2}-\d{2}$/.test(wdParam ?? '') ? (wdParam as string) : currentDateStr;
  const fyParam = Number(searchParams.get('fy'));
  const initFiscalYear = Number.isFinite(fyParam) && fyParam > 2000 ? fyParam : currentFiscalYear;
  const cyParam = Number(searchParams.get('cy'));
  const initCalendarYear =
    Number.isFinite(cyParam) && cyParam > 2000 ? cyParam : todayDate.getFullYear();
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const initCustomRange: DateRange = {
    from: /^\d{4}-\d{2}-\d{2}$/.test(fromParam ?? '')
      ? (fromParam as string)
      : `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-01`,
    to: /^\d{4}-\d{2}-\d{2}$/.test(toParam ?? '') ? (toParam as string) : currentDateStr,
  };

  const [storeScope, setStoreScope] = useState<string>(initStore);
  const [period, setPeriod] = useState<Period>(initPeriod);
  const [ld, setLd] = useState<LDFilter>(initLd);
  const [selectedYearMonth, setSelectedYearMonth] = useState(initYearMonth);
  const [selectedWeekDate, setSelectedWeekDate] = useState(initWeekDate);
  const [yearMode, setYearMode] = useState<YearMode>(initYearMode);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(initFiscalYear);
  const [selectedCalendarYear, setSelectedCalendarYear] = useState(initCalendarYear);
  const [customRange, setCustomRange] = useState<DateRange>(initCustomRange);
  const selectedIsoWeek = dateStrToIsoWeek(selectedWeekDate);

  // state 変更を URL に反映（必要なパラメータのみ書き、デフォルト値は省略）
  useEffect(() => {
    const next = new URLSearchParams();
    if (storeScope !== 'all') next.set('store', storeScope);
    next.set('period', period);
    if (ld !== 'all') next.set('ld', ld);
    if (period === 'month' || (period === 'year' && yearMode === 'rolling12')) {
      next.set('ym', selectedYearMonth);
    }
    if (period === 'week') {
      next.set('wd', selectedWeekDate);
    }
    if (period === 'year') {
      next.set('ymode', yearMode);
      if (yearMode === 'fiscal') next.set('fy', String(selectedFiscalYear));
      if (yearMode === 'calendar') next.set('cy', String(selectedCalendarYear));
    }
    if (period === 'custom') {
      next.set('from', customRange.from);
      next.set('to', customRange.to);
    }
    // 既存と同じなら setSearchParams しない（無限ループ防止）
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    storeScope,
    period,
    ld,
    selectedYearMonth,
    selectedWeekDate,
    yearMode,
    selectedFiscalYear,
    selectedCalendarYear,
    customRange.from,
    customRange.to,
    searchParams,
    setSearchParams,
  ]);

  // 店舗マスタ取得
  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];
  const targetStores = storeScope === 'all' ? stores : stores.filter((s) => s.id === storeScope);

  // 期間レンジ（DateRange と月配列）
  //   week は ISO 週ではなく「selectedWeekDate を末日とした過去 7 日間」（売上登録起点）
  const periodRange: DateRange = useMemo(() => {
    if (period === 'month') return periodMonthRange(selectedYearMonth);
    if (period === 'week') {
      const d = new Date(`${selectedWeekDate}T00:00:00Z`);
      const fromD = new Date(d);
      fromD.setUTCDate(fromD.getUTCDate() - 6);
      return {
        from: fromD.toISOString().slice(0, 10),
        to: selectedWeekDate,
      };
    }
    if (period === 'custom') return customRange;
    // year
    if (yearMode === 'fiscal') return fiscalYearRange(selectedFiscalYear, FISCAL_START_MONTH);
    if (yearMode === 'calendar') return calendarYearRange(selectedCalendarYear);
    // rolling12: 直近12ヶ月（基準月から遡る）
    const back = monthsBack(12, selectedYearMonth);
    return {
      from: `${back[0]}-01`,
      to: periodMonthRange(back[back.length - 1]).to,
    };
  }, [period, selectedYearMonth, selectedWeekDate, customRange, yearMode, selectedFiscalYear, selectedCalendarYear]);

  // year モードのみ月別ポイントなので months 配列を計算する
  const months = period === 'year' ? monthsInRange(periodRange) : [];
  const isoWeeks = period === 'week' ? weeksBack(12, selectedIsoWeek) : [];

  // 期間内の実日数（今日以降は除外）— 表示用
  const periodDayCount = effectiveDayCount(periodRange, todayDate);

  // ============================================================================
  // 前期（比較対象）のレンジ計算
  //   week: 前週／month: 前月／year: 前年（同モード）／custom: 比較なし
  // ============================================================================
  interface PrevRangeInfo {
    range: DateRange;
    months: string[];
    isoWeek: string | null;
    label: string;
  }
  const prevRangeInfo: PrevRangeInfo | null = useMemo(() => {
    if (period === 'week') {
      // 「過去 7 日間」のさらに前の 7 日間
      const d = new Date(`${selectedWeekDate}T00:00:00Z`);
      const prevToD = new Date(d);
      prevToD.setUTCDate(prevToD.getUTCDate() - 7);
      const prevFromD = new Date(prevToD);
      prevFromD.setUTCDate(prevFromD.getUTCDate() - 6);
      return {
        range: {
          from: prevFromD.toISOString().slice(0, 10),
          to: prevToD.toISOString().slice(0, 10),
        },
        months: [],
        isoWeek: null,
        label: '前 7 日間',
      };
    }
    if (period === 'custom') {
      // カスタム期間と同じ長さの直前期間を前期にする
      const fromMs = new Date(`${customRange.from}T00:00:00Z`).getTime();
      const toMs = new Date(`${customRange.to}T00:00:00Z`).getTime();
      const days = Math.max(1, Math.floor((toMs - fromMs) / 86400000) + 1);
      const prevToD = new Date(fromMs);
      prevToD.setUTCDate(prevToD.getUTCDate() - 1);
      const prevFromD = new Date(prevToD);
      prevFromD.setUTCDate(prevFromD.getUTCDate() - (days - 1));
      return {
        range: {
          from: prevFromD.toISOString().slice(0, 10),
          to: prevToD.toISOString().slice(0, 10),
        },
        months: [],
        isoWeek: null,
        label: `直前 ${days} 日間`,
      };
    }
    if (period === 'month') {
      const [y, m] = selectedYearMonth.split('-').map(Number);
      const d = new Date(y, m - 2, 1);
      const prevYM = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return {
        range: periodMonthRange(prevYM),
        months: [prevYM],
        isoWeek: null,
        label: '前月',
      };
    }
    // year
    if (yearMode === 'rolling12') {
      const [y, m] = selectedYearMonth.split('-').map(Number);
      const d = new Date(y - 1, m - 1, 1);
      const baseYM = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const back = monthsBack(12, baseYM);
      return {
        range: { from: `${back[0]}-01`, to: periodMonthRange(back[back.length - 1]).to },
        months: back,
        isoWeek: null,
        label: '前年同期',
      };
    }
    if (yearMode === 'calendar') {
      const py = selectedCalendarYear - 1;
      const r = calendarYearRange(py);
      return {
        range: r,
        months: monthsInRange(r),
        isoWeek: null,
        label: `${py} 年`,
      };
    }
    // fiscal
    const py = selectedFiscalYear - 1;
    const r = fiscalYearRange(py, FISCAL_START_MONTH);
    return {
      range: r,
      months: monthsInRange(r),
      isoWeek: null,
      label: `${py} 年度`,
    };
  }, [period, selectedWeekDate, selectedYearMonth, yearMode, selectedFiscalYear, selectedCalendarYear, customRange.from, customRange.to]);

  // monthlySummary（年間/月間モード用、12ヶ月分）
  const monthlyQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['monthlySummary', s.id, months[0] ?? '', months[months.length - 1] ?? ''],
      queryFn: () =>
        gql<{ monthlySummary: PeriodSummary[] }>(MONTHLY_SUMMARY_QUERY, {
          storeId: s.id,
          fromYearMonth: months[0],
          toYearMonth: months[months.length - 1],
        }),
      enabled: !!s.id && period !== 'week',
    })),
  });

  // weeklySummary（週間モード用、12週分）
  const weeklyQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['weeklySummary', s.id, isoWeeks[0] ?? '', isoWeeks[isoWeeks.length - 1] ?? ''],
      queryFn: () =>
        gql<{ weeklySummary: PeriodSummary[] }>(WEEKLY_SUMMARY_QUERY, {
          storeId: s.id,
          fromIsoWeek: isoWeeks[0],
          toIsoWeek: isoWeeks[isoWeeks.length - 1],
        }),
      enabled: !!s.id && period === 'week',
    })),
  });

  // costEntries（PL 用、期間内）— periodRange に統一
  const costQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['costEntries', s.id, periodRange.from, periodRange.to],
      queryFn: () =>
        gql<{ costEntries: CostEntry[] }>(COST_ENTRIES_QUERY, {
          storeId: s.id,
          from: periodRange.from,
          to: periodRange.to,
        }),
      enabled: !!s.id,
    })),
  });

  // 固定費（家賃含む）の月額固定値を全店舗から取得
  const fixedCostQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['fixedCostItems', s.id],
      queryFn: () =>
        gql<{ fixedCostItems: FixedCostItem[] }>(FIXED_COST_ITEMS_QUERY, { storeId: s.id }),
      enabled: isAdmin && !!s.id,
    })),
  });

  // 人件費（月額固定値、暫定）— 登録があれば人件費 KPI はこちら優先
  const laborCostQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['laborCostItems', s.id],
      queryFn: () =>
        gql<{ laborCostItems: LaborCostItem[] }>(LABOR_COST_ITEMS_QUERY, { storeId: s.id }),
      enabled: isAdmin && !!s.id,
    })),
  });

  // 借入（全社固定）
  const loansQ = useQuery({
    queryKey: ['loans'],
    queryFn: () => gql<{ loans: Loan[] }>(LOANS_QUERY),
    enabled: isAdmin,
  });
  const loans = loansQ.data?.loans ?? [];

  // スタッフマスタ（全社）
  const staffQ = useQuery({
    queryKey: ['staff'],
    queryFn: () => gql<{ staff: Staff[] }>(STAFF_QUERY),
    enabled: isAdmin,
  });
  const staffList: Staff[] = staffQ.data?.staff ?? [];

  // シフト（人件費計算用）— 期間範囲をそのまま使う
  const shiftRange: DateRange = periodRange;

  const shiftQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['shifts', s.id, shiftRange.from, shiftRange.to],
      queryFn: () =>
        gql<{ shifts: Shift[] }>(SHIFTS_QUERY, {
          storeId: s.id,
          from: shiftRange.from,
          to: shiftRange.to,
        }),
      enabled: isAdmin && !!s.id,
    })),
  });

  const allShifts: Shift[] = useMemo(
    () => shiftQueries.flatMap((q) => q.data?.shifts ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftQueries.map((q) => q.dataUpdatedAt).join('|')],
  );

  // トランザクションは会計日のキーで保存される。営業日の末日に来店した取引の
  // 翌日未明の会計も取得し、表示期間への帰属は来店日時で判定する。
  const txnQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['txns-daily', s.id, periodRange.from, nextDate(periodRange.to)],
      queryFn: () =>
        gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
          storeId: s.id,
          from: periodRange.from,
          to: nextDate(periodRange.to),
        }),
      enabled: !!s.id && (period === 'month' || period === 'week' || period === 'custom'),
    })),
  });

  // 最新売上日プローブ（直近 60 日の取引から Airレジ営業日を求める）
  //   週間モードのデフォルトを「最新売上日を末日とした過去 7 日間」にするために使う
  const probeRange = useMemo(() => {
    const fromD = new Date(todayDate);
    fromD.setDate(fromD.getDate() - 60);
    return {
      from: fromD.toISOString().slice(0, 10),
      to: currentDateStr,
    };
  }, []);
  const latestProbeQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['probeLatestTxn', s.id, probeRange.from, probeRange.to],
      queryFn: () =>
        gql<{ transactions: Pick<Transaction, 'storeId' | 'visitDate' | 'visitTime' | 'accountDate' | 'accountTime'>[] }>(LATEST_TXN_DATES_QUERY, {
          storeId: s.id,
          from: probeRange.from,
          to: probeRange.to,
        }),
      enabled: !!s.id,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const latestSalesDate = useMemo(() => {
    const storeById = new Map(stores.map((store) => [store.id, store]));
    const all = latestProbeQueries.flatMap((q) =>
      (q.data?.transactions ?? []).map((txn) =>
        airRegiBusinessDate(txn, storeById.get(txn.storeId)?.airregiBusinessDayStart),
      ),
    );
    if (all.length === 0) return null;
    return all.sort().at(-1) ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesQ.dataUpdatedAt, latestProbeQueries.map((q) => q.dataUpdatedAt).join('|')]);

  // 初回 1 度だけ、selectedWeekDate を最新売上日に合わせる。
  // ただし URL に wd が指定されている場合は URL を尊重し、上書きしない。
  const [didInitWeekDate, setDidInitWeekDate] = useState(!!wdParam);
  useEffect(() => {
    if (didInitWeekDate || !latestSalesDate) return;
    setSelectedWeekDate(latestSalesDate);
    setDidInitWeekDate(true);
  }, [latestSalesDate, didInitWeekDate]);

  const allTxns: Transaction[] = useMemo(
    () => txnQueries.flatMap((q) => q.data?.transactions ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [txnQueries.map((q) => q.dataUpdatedAt).join('|')],
  );

  // ============================================================================
  // 前期データ取得（カスタム期間以外）
  // ============================================================================
  const prevRange = prevRangeInfo?.range ?? null;
  const prevMonths = prevRangeInfo?.months ?? [];
  const prevIsoWeek = prevRangeInfo?.isoWeek ?? null;

  // 前期の monthlySummary（month/year モード）
  const prevMonthlyQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['monthlySummaryPrev', s.id, prevMonths[0] ?? '', prevMonths[prevMonths.length - 1] ?? ''],
      queryFn: () =>
        gql<{ monthlySummary: PeriodSummary[] }>(MONTHLY_SUMMARY_QUERY, {
          storeId: s.id,
          fromYearMonth: prevMonths[0],
          toYearMonth: prevMonths[prevMonths.length - 1],
        }),
      enabled: !!s.id && period !== 'week' && period !== 'custom' && prevMonths.length > 0,
    })),
  });

  // 前期の weeklySummary（week モード）
  const prevWeeklyQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['weeklySummaryPrev', s.id, prevIsoWeek ?? ''],
      queryFn: () =>
        gql<{ weeklySummary: PeriodSummary[] }>(WEEKLY_SUMMARY_QUERY, {
          storeId: s.id,
          fromIsoWeek: prevIsoWeek,
          toIsoWeek: prevIsoWeek,
        }),
      enabled: !!s.id && period === 'week' && !!prevIsoWeek,
    })),
  });

  // 前期の costEntries
  const prevCostQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['costEntriesPrev', s.id, prevRange?.from ?? '', prevRange?.to ?? ''],
      queryFn: () =>
        gql<{ costEntries: CostEntry[] }>(COST_ENTRIES_QUERY, {
          storeId: s.id,
          from: prevRange?.from,
          to: prevRange?.to,
        }),
      enabled: !!s.id && !!prevRange,
    })),
  });

  // 前期のシフト
  const prevShiftQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['shiftsPrev', s.id, prevRange?.from ?? '', prevRange?.to ?? ''],
      queryFn: () =>
        gql<{ shifts: Shift[] }>(SHIFTS_QUERY, {
          storeId: s.id,
          from: prevRange?.from,
          to: prevRange?.to,
        }),
      enabled: isAdmin && !!s.id && !!prevRange,
    })),
  });

  // 前期のトランザクション（week / month / custom モード時、日別 buildPoints 用）
  const prevTxnQueries = useQueries({
    queries: targetStores.map((s) => ({
      queryKey: ['txns-daily-prev', s.id, prevRange?.from ?? '', prevRange ? nextDate(prevRange.to) : ''],
      queryFn: () =>
        gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
          storeId: s.id,
          from: prevRange?.from,
          to: prevRange ? nextDate(prevRange.to) : undefined,
        }),
      enabled:
        !!s.id &&
        (period === 'month' || period === 'week' || period === 'custom') &&
        !!prevRange,
    })),
  });

  const allPrevSummaries: PeriodSummary[] = useMemo(() => {
    if (!prevRangeInfo) return [];
    if (period === 'week') {
      return prevWeeklyQueries.flatMap((q) => q.data?.weeklySummary ?? []);
    }
    return prevMonthlyQueries.flatMap((q) => q.data?.monthlySummary ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    period,
    !!prevRangeInfo,
    prevMonthlyQueries.map((q) => q.dataUpdatedAt).join('|'),
    prevWeeklyQueries.map((q) => q.dataUpdatedAt).join('|'),
  ]);

  const allPrevCosts: CostEntry[] = useMemo(
    () => prevCostQueries.flatMap((q) => q.data?.costEntries ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevCostQueries.map((q) => q.dataUpdatedAt).join('|')],
  );
  const trustedPrevCosts = allPrevCosts.filter((c) => c.reportInput === 'あり');

  const allPrevShifts: Shift[] = useMemo(
    () => prevShiftQueries.flatMap((q) => q.data?.shifts ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevShiftQueries.map((q) => q.dataUpdatedAt).join('|')],
  );

  const allPrevTxns: Transaction[] = useMemo(
    () => prevTxnQueries.flatMap((q) => q.data?.transactions ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevTxnQueries.map((q) => q.dataUpdatedAt).join('|')],
  );

  // ベンチマーク
  const benchmarkQ = useQuery({
    queryKey: ['benchmark'],
    queryFn: () => gql<{ benchmark: Benchmark }>(BENCHMARK_QUERY),
    enabled: isAdmin,
  });
  const benchmark: Benchmark = benchmarkQ.data?.benchmark ?? {
    foodCostPct: 30,
    laborCostPct: 30,
    flPct: 55,
    rentPct: 10,
    otherPct: 20,
    profitPct: 10,
  };

  // ============================================================================
  // 集計ロジック
  // ============================================================================

  // 全店舗合算サマリ（期間内すべて）
  const allSummaries: PeriodSummary[] = useMemo(() => {
    if (period === 'week') {
      return weeklyQueries.flatMap((q) => q.data?.weeklySummary ?? []);
    }
    return monthlyQueries.flatMap((q) => q.data?.monthlySummary ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    period,
    monthlyQueries.map((q) => q.dataUpdatedAt).join('|'),
    weeklyQueries.map((q) => q.dataUpdatedAt).join('|'),
  ]);

  // F: costEntries 集計（reportInput="あり" のみ採用）
  // L（人件費）は Airシフト ＋ スタッフマスタから別途計算するため、ここでは「原価」だけ対象
  const allCosts: CostEntry[] = useMemo(
    () => costQueries.flatMap((q) => q.data?.costEntries ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [costQueries.map((q) => q.dataUpdatedAt).join('|')],
  );
  const trustedCosts = allCosts.filter((c) => c.reportInput === 'あり');

  // 固定費（月額固定）— 家賃 (rent) と それ以外を分離
  const allFixedCostItems: FixedCostItem[] = useMemo(
    () => fixedCostQueries.flatMap((q) => q.data?.fixedCostItems ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fixedCostQueries.map((q) => q.dataUpdatedAt).join('|')],
  );
  const monthlyRentTotal = allFixedCostItems
    .filter((it) => it.itemKey === 'rent')
    .reduce((s, it) => s + it.amount, 0);
  const monthlyOtherTotal = allFixedCostItems
    .filter((it) => it.itemKey !== 'rent')
    .reduce((s, it) => s + it.amount, 0);

  // 人件費（月額固定値）— 全店舗合計の月額。登録があればこちらを優先
  const allLaborCostItems: LaborCostItem[] = useMemo(
    () => laborCostQueries.flatMap((q) => q.data?.laborCostItems ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [laborCostQueries.map((q) => q.dataUpdatedAt).join('|')],
  );
  const monthlyLaborTotal = allLaborCostItems.reduce((s, it) => s + it.amount, 0);
  const useFixedLabor = monthlyLaborTotal > 0;

  // 各種金額の合算は points を計算してから pointsTotals 経由で取得する
  // （売上のない月のコストを除外するため）
  const targetStoreIds = targetStores.map((s) => s.id);

  // KPI 値はすべて pointsTotals から導出する（週間=ローリング 7 日対応のため）。
  // L/D 按分は points 内で完結しているので ldRatio の計算は不要。

  // ============================================================================
  // グラフデータ（期間ごとに 12 ポイント）
  // ============================================================================
  interface PointData {
    label: string;
    sales: number;
    f: number;
    l: number;
    rent: number;
    other: number;
    profit: number;
    flPct: number | null;
    avgSpend: number | null;
    customers: number;
    partyCount: number;
    foodQty: number;
    drinkQty: number;
  }

  const storeById = new Map(stores.map((store) => [store.id, store]));

  // 当期／前期共通の points 計算ロジック
  //   periodMode: 'day' = 月間／週間 → 日別ポイント、'month' = 年間／カスタム → 月別ポイント
  const buildPoints = (
    periodMode: 'day' | 'month',
    range: DateRange,
    monthsArr: string[],
    summaries: PeriodSummary[],
    costsArr: CostEntry[],
    txnsArr: Transaction[],
    shiftsArr: Shift[],
  ): PointData[] => {
    if (periodMode === 'day') {
      const dayList: string[] = [];
      const fromMs = new Date(`${range.from}T00:00:00Z`).getTime();
      const toMs = new Date(`${range.to}T00:00:00Z`).getTime();
      for (let ms = fromMs; ms <= toMs; ms += 86400000) {
        dayList.push(new Date(ms).toISOString().slice(0, 10));
      }
      // 家賃／その他固定費／月額固定の人件費は契約上の月額固定費。
      // 月の日数（28〜31日）で割って各日の按分額を出すことで、
      // 「月単位の家賃合計＝月額」が成立する（30 日固定だと月によって誤差が出る）。
      const daysInMonth = (yearMonth: string): number => {
        const [y, m] = yearMonth.split('-').map(Number);
        return new Date(Date.UTC(y, m, 0)).getUTCDate();
      };
      const todayStrCached = todayStr(todayDate);

      return dayList.map((dateStr) => {
        const txnsToday = txnsArr.filter((tx) =>
          airRegiBusinessDate(tx, storeById.get(tx.storeId)?.airregiBusinessDayStart) === dateStr,
        );
        let salesAll = 0;
        let salesLd = 0;
        let custAll = 0;
        let custLd = 0;
        let foodQty = 0;
        let drinkQty = 0;
        let partyAll = 0;
        let partyLd = 0;
        for (const tx of txnsToday) {
          const seg = airRegiService(tx, storeById.get(tx.storeId));
          const t = tx.total ?? 0;
          const c = tx.customerCount ?? 0;
          salesAll += t;
          custAll += c;
          partyAll += 1;
          if (ld === 'all' || seg === ld) {
            salesLd += t;
            custLd += c;
            partyLd += 1;
            for (const it of tx.items ?? []) {
              const cat = (it.category ?? '').toString();
              const name = (it.menuName ?? '').toString();
              const drinkKw = ['ドリンク', '飲料', 'ソフトドリンク', 'アルコール', '酒', 'ビール', 'ハイボール', 'カクテル', 'ワイン', '日本酒', '焼酎', 'サワー'];
              // カテゴリでマッチしない場合は menuName でフォールバック判定
              // （ジャーナル履歴由来の取引は category が常に null）
              const isDrink =
                drinkKw.some((k) => cat.includes(k)) ||
                (!cat && drinkKw.some((k) => name.includes(k)));
              if (isDrink) drinkQty += it.quantity ?? 0;
              else foodQty += it.quantity ?? 0;
            }
          }
        }
        void custAll;
        void partyAll;
        // L/D 切替の按分比：全時間帯モード時は ratio=1。L/D 切替時は当日売上比。
        const ratio = ld === 'all' || salesAll <= 0 ? 1 : salesLd / salesAll;

        // 今日以前の日 → 営業日として家賃／固定費／月額固定人件費を按分計上
        // 未来日（取込待ちや今後の日付）→ 0
        const isPast = dateStr <= todayStrCached;

        const fAmtP = costsArr
          .filter((c) => c.category === '原価' && c.date === dateStr)
          .reduce((s, c) => s + c.amount, 0) * ratio;
        // その日が含まれる月の日数で按分（家賃は月の日数によらず月額が一定のため）
        const dim = daysInMonth(dateStr.slice(0, 7));
        const dailyRent = monthlyRentTotal / dim;
        const dailyOther = monthlyOtherTotal / dim;
        const dailyFixedLabor = monthlyLaborTotal / dim;
        // 月額固定値モードは契約上の月額を日割りし、未設定時は実労働から算出する。
        const lAmtP = useFixedLabor
          ? isPast
            ? dailyFixedLabor * ratio
            : 0
          : computeLaborCostForDateRange(
              staffList,
              shiftsArr,
              dateStr,
              dateStr,
              targetStoreIds,
            ).total * ratio;
        const rentP = isPast ? dailyRent * ratio : 0;
        const otherP = isPast ? dailyOther * ratio : 0;
        const profit = salesLd - fAmtP - lAmtP - rentP - otherP;
        const [, m, d] = dateStr.split('-');
        const wday = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
        const wstr = ['日', '月', '火', '水', '木', '金', '土'][wday];
        // 曜日付きラベル：週間=「5/3(土)」、月間/カスタム=「3日(土)」
        const label =
          period === 'week'
            ? `${Number(m)}/${Number(d)}(${wstr})`
            : `${Number(d)}日(${wstr})`;
        return {
          label,
          sales: salesLd,
          f: fAmtP,
          l: lAmtP,
          rent: rentP,
          other: otherP,
          profit,
          flPct: salesLd > 0 ? ((fAmtP + lAmtP) / salesLd) * 100 : null,
          avgSpend: custLd > 0 ? salesLd / custLd : null,
          customers: custLd,
          partyCount: partyLd,
          foodQty,
          drinkQty,
        };
      });
    }

    // 月別
    const monthDaysMap = monthlyDaysInRange(range, todayDate);
    const daysInMonthMonth = (yearMonth: string): number => {
      const [y, m] = yearMonth.split('-').map(Number);
      return new Date(Date.UTC(y, m, 0)).getUTCDate();
    };
    return monthsArr.map((ym) => {
      const days = monthDaysMap[ym] ?? 0;
      // 月の日数で按分（完全月なら factor=1.0、月途中なら経過日数/月日数）
      const factor = days / daysInMonthMonth(ym);
      const summariesForMonth = summaries.filter((s) => s.periodKey === `MONTH#${ym}`);
      const aggLd = sumSummaries(summariesForMonth, ld);
      const aggAll = sumSummaries(summariesForMonth, 'all');
      const hasSales = aggAll.sales > 0;
      const ratio = ld === 'all' || aggAll.sales <= 0 ? 1 : aggLd.sales / aggAll.sales;
      const fAmtP = !hasSales ? 0 : costsArr
        .filter((c) => c.category === '原価' && c.date.slice(0, 7) === ym)
        .reduce((s, c) => s + c.amount, 0) * ratio;
      const monthShifts = shiftsArr.filter((s) => s.date.startsWith(ym));
      const lAmtP = !hasSales
        ? 0
        : (useFixedLabor
            ? monthlyLaborTotal * factor
            : computeLaborCostForMonth(staffList, monthShifts, targetStoreIds).total) * ratio;
      const rentP = !hasSales ? 0 : monthlyRentTotal * factor * ratio;
      const otherP = !hasSales ? 0 : monthlyOtherTotal * factor * ratio;
      const profit = aggLd.sales - fAmtP - lAmtP - rentP - otherP;
      return {
        label: ym,
        sales: aggLd.sales,
        f: fAmtP,
        l: lAmtP,
        rent: rentP,
        other: otherP,
        profit,
        flPct: aggLd.sales > 0 ? ((fAmtP + lAmtP) / aggLd.sales) * 100 : null,
        avgSpend: aggLd.customerCount > 0 ? aggLd.sales / aggLd.customerCount : null,
        customers: aggLd.customerCount,
        partyCount: aggLd.partyCount,
        foodQty: aggLd.foodQty,
        drinkQty: aggLd.drinkQty,
      };
    });
  };

  const points: PointData[] = useMemo(() => {
    // year モードのみ月別、それ以外（week / month / custom）は日別
    const mode: 'day' | 'month' = period === 'year' ? 'month' : 'day';
    return buildPoints(
      mode,
      periodRange,
      months,
      allSummaries,
      trustedCosts,
      allTxns,
      allShifts,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    period,
    months.join('|'),
    isoWeeks.join('|'),
    allSummaries,
    trustedCosts,
    monthlyRentTotal,
    monthlyOtherTotal,
    useFixedLabor,
    monthlyLaborTotal,
    ld,
    allShifts,
    allTxns,
    storesQ.dataUpdatedAt,
    staffList,
    targetStoreIds.join('|'),
    periodRange.from,
    periodRange.to,
  ]);

  // 前期 points
  const prevPoints: PointData[] = useMemo(() => {
    if (!prevRangeInfo) return [];
    const mode: 'day' | 'month' = period === 'year' ? 'month' : 'day';
    return buildPoints(
      mode,
      prevRangeInfo.range,
      prevRangeInfo.months,
      allPrevSummaries,
      trustedPrevCosts,
      allPrevTxns,
      allPrevShifts,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    period,
    prevRangeInfo?.range.from,
    prevRangeInfo?.range.to,
    prevMonths.join('|'),
    allPrevSummaries,
    trustedPrevCosts,
    monthlyRentTotal,
    monthlyOtherTotal,
    useFixedLabor,
    monthlyLaborTotal,
    ld,
    allPrevShifts,
    allPrevTxns,
    storesQ.dataUpdatedAt,
    staffList,
    targetStoreIds.join('|'),
  ]);

  // KPI 値（期間絞り込み後 × L/D 按分）
  // points 合計から導出することで、売上のない月のコストが除外される
  const sumPoints = (arr: PointData[]) =>
    arr.reduce(
      (acc, p) => ({
        sales: acc.sales + p.sales,
        f: acc.f + p.f,
        l: acc.l + p.l,
        rent: acc.rent + p.rent,
        other: acc.other + p.other,
        customers: acc.customers + p.customers,
        partyCount: acc.partyCount + p.partyCount,
      }),
      { sales: 0, f: 0, l: 0, rent: 0, other: 0, customers: 0, partyCount: 0 },
    );
  const pointsTotals = useMemo(() => sumPoints(points), [points]);
  const prevPointsTotals = useMemo(() => sumPoints(prevPoints), [prevPoints]);

  // すべてのモードで pointsTotals を権威ソースとする（週間モードのISO週ズレ問題を回避）
  const sales = pointsTotals.sales;
  const fAmt = pointsTotals.f;
  const lAmt = pointsTotals.l;
  const rentAmt = pointsTotals.rent;
  const otherAmt = pointsTotals.other;
  const profitAmt = sales - fAmt - lAmt - rentAmt - otherAmt;
  const totalCustomers = pointsTotals.customers;
  const totalPartyCount = pointsTotals.partyCount;
  const fPct = sales > 0 ? (fAmt / sales) * 100 : null;
  const lPct = sales > 0 ? (lAmt / sales) * 100 : null;
  const flPct = sales > 0 ? ((fAmt + lAmt) / sales) * 100 : null;
  const rentPct = sales > 0 ? (rentAmt / sales) * 100 : null;
  const otherPct = sales > 0 ? (otherAmt / sales) * 100 : null;
  const profitPct = sales > 0 ? (profitAmt / sales) * 100 : null;

  // 前期 KPI（pointsTotals を権威ソース）
  const prevSales = prevPointsTotals.sales;
  const prevFAmt = prevPointsTotals.f;
  const prevLAmt = prevPointsTotals.l;
  const prevRentAmt = prevPointsTotals.rent;
  const prevOtherAmt = prevPointsTotals.other;
  const prevProfitAmt = prevSales - prevFAmt - prevLAmt - prevRentAmt - prevOtherAmt;
  const prevTotalCustomers = prevPointsTotals.customers;
  const prevTotalPartyCount = prevPointsTotals.partyCount;
  const prevFPct = prevSales > 0 ? (prevFAmt / prevSales) * 100 : null;
  const prevLPct = prevSales > 0 ? (prevLAmt / prevSales) * 100 : null;
  const prevFlPct = prevSales > 0 ? ((prevFAmt + prevLAmt) / prevSales) * 100 : null;
  const prevRentPct = prevSales > 0 ? (prevRentAmt / prevSales) * 100 : null;
  const prevOtherPct = prevSales > 0 ? (prevOtherAmt / prevSales) * 100 : null;
  const prevProfitPct = prevSales > 0 ? (prevProfitAmt / prevSales) * 100 : null;
  void prevFPct;
  void prevLPct;
  void prevFlPct;
  void prevRentPct;
  void prevOtherPct;
  void prevProfitPct;

  // 前期比 % 差分（前期 0 や前期データなしの場合は null）
  const diffPct = (cur: number, prev: number): number | null => {
    if (!prev || !Number.isFinite(prev)) return null;
    return ((cur - prev) / Math.abs(prev)) * 100;
  };
  const hasPrev = !!prevRangeInfo && prevSales > 0;

  // 借入サマリ
  const loanSummary = useMemo(() => {
    const outstanding = loans.reduce((s, l) => s + (l.outstanding ?? 0), 0);
    const monthly = loans.reduce((s, l) => s + (l.monthlyPayment ?? 0), 0);
    const longest =
      loans.length > 0
        ? Math.max(
            ...loans.map((l) => (l.monthlyPayment > 0 ? Math.ceil(l.outstanding / l.monthlyPayment) : 0)),
          )
        : 0;
    return { outstanding, monthly, longest };
  }, [loans]);

  // 既存セクション用: 最新月の単一サマリ
  const latestMonthlySummary: PeriodSummary | undefined = useMemo(() => {
    if (period === 'week') return undefined;
    return allSummaries.find((s) => s.periodKey === `MONTH#${selectedYearMonth}`);
  }, [allSummaries, period, selectedYearMonth]);

  // 好EN にランチ無し → ランチ切替ボタンを無効化する判定
  const showLunchToggle = stores.some((s) => s.lunchStart && s.lunchEnd);

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading" style={pageHeader}>
        <h1 style={pageTitle}>トップページ</h1>
      </div>

      {/* 共通フィルターバー */}
      <section className="dashboard-filter" style={filterBar}>
        <div className="dashboard-filter-group" style={filterGroup}>
          <span className="dashboard-filter-label" style={filterLabel}>店舗</span>
          <button
            type="button"
            onClick={() => setStoreScope('all')}
            className={storeScope === 'all' ? 'filter-option filter-option-active' : 'filter-option'}
            style={storeScope === 'all' ? filterChipActive : filterChip}
          >
            全社合算
          </button>
          {stores.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStoreScope(s.id)}
              className={storeScope === s.id ? 'filter-option filter-option-active' : 'filter-option'}
              style={storeScope === s.id ? filterChipActive : filterChip}
            >
              {s.name}
            </button>
          ))}
        </div>

        <div className="dashboard-filter-group" style={filterGroup}>
          <span className="dashboard-filter-label" style={filterLabel}>期間</span>
          <button
            type="button"
            onClick={() => setPeriod('week')}
            className={period === 'week' ? 'filter-option filter-option-active' : 'filter-option'}
            style={period === 'week' ? filterChipActive : filterChip}
          >
            週間
          </button>
          <button
            type="button"
            onClick={() => setPeriod('month')}
            className={period === 'month' ? 'filter-option filter-option-active' : 'filter-option'}
            style={period === 'month' ? filterChipActive : filterChip}
          >
            月間
          </button>
          <button
            type="button"
            onClick={() => setPeriod('year')}
            className={period === 'year' ? 'filter-option filter-option-active' : 'filter-option'}
            style={period === 'year' ? filterChipActive : filterChip}
          >
            年間
          </button>
          <button
            type="button"
            onClick={() => setPeriod('custom')}
            className={period === 'custom' ? 'filter-option filter-option-active' : 'filter-option'}
            style={period === 'custom' ? filterChipActive : filterChip}
          >
            カスタム
          </button>
        </div>

        {/* 期間モード別の入力欄 */}
        <div className="dashboard-filter-group dashboard-filter-range" style={filterGroup}>
          {period === 'month' && (
            <input
              type="month"
              value={selectedYearMonth}
              onChange={(e) => setSelectedYearMonth(e.target.value)}
              style={inputInline}
            />
          )}

          {period === 'year' && (
            <>
              <select
                value={yearMode}
                onChange={(e) => setYearMode(e.target.value as YearMode)}
                style={inputInline}
              >
                <option value="rolling12">直近12ヶ月（基準月から）</option>
                <option value="calendar">暦年（1月〜12月）</option>
                <option value="fiscal">年度（8月〜翌7月）</option>
              </select>
              {yearMode === 'rolling12' && (
                <input
                  type="month"
                  value={selectedYearMonth}
                  onChange={(e) => setSelectedYearMonth(e.target.value)}
                  style={inputInline}
                  title="基準月"
                />
              )}
              {yearMode === 'calendar' && (
                <select
                  value={selectedCalendarYear}
                  onChange={(e) => setSelectedCalendarYear(Number(e.target.value))}
                  style={inputInline}
                >
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <option key={y} value={y}>{y} 年</option>
                  ))}
                </select>
              )}
              {yearMode === 'fiscal' && (
                <select
                  value={selectedFiscalYear}
                  onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
                  style={inputInline}
                >
                  {[2023, 2024, 2025, 2026].map((y) => (
                    <option key={y} value={y}>
                      {y} 年度（{y}/8 〜 {y + 1}/7）
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

          {period === 'week' && (
            <>
              <button
                type="button"
                onClick={() => {
                  const d = new Date(`${selectedWeekDate}T00:00:00Z`);
                  d.setUTCDate(d.getUTCDate() - 7);
                  setSelectedWeekDate(d.toISOString().slice(0, 10));
                }}
                style={navBtn}
                title="前週"
              >
                ◀
              </button>
              <input
                type="date"
                value={selectedWeekDate}
                onChange={(e) => setSelectedWeekDate(e.target.value || currentDateStr)}
                style={inputInline}
              />
              <button
                type="button"
                onClick={() => {
                  const d = new Date(`${selectedWeekDate}T00:00:00Z`);
                  d.setUTCDate(d.getUTCDate() + 7);
                  setSelectedWeekDate(d.toISOString().slice(0, 10));
                }}
                style={navBtn}
                title="次週"
              >
                ▶
              </button>
              <span style={{ fontSize: '0.78rem', color: '#495057' }}>
                {formatRangeLabel(periodRange)}
              </span>
            </>
          )}

          {period === 'custom' && (
            <>
              <input
                type="date"
                value={customRange.from}
                onChange={(e) =>
                  setCustomRange((r) => ({ ...r, from: e.target.value || r.from }))
                }
                style={inputInline}
                title="開始日"
              />
              <span style={{ fontSize: '0.85rem', color: '#495057' }}>〜</span>
              <input
                type="date"
                value={customRange.to}
                onChange={(e) =>
                  setCustomRange((r) => ({ ...r, to: e.target.value || r.to }))
                }
                style={inputInline}
                title="終了日"
              />
            </>
          )}

          <span style={{ fontSize: '0.75rem', color: '#868e96', marginLeft: 8 }}>
            集計期間: <strong>{formatRangeLabel(periodRange)}</strong>（実 {periodDayCount} 日）
          </span>
        </div>

        <div className="dashboard-filter-group" style={filterGroup}>
          <span className="dashboard-filter-label" style={filterLabel}>時間帯</span>
          <button
            type="button"
            onClick={() => setLd('all')}
            className={ld === 'all' ? 'filter-option filter-option-active' : 'filter-option'}
            style={ld === 'all' ? filterChipActive : filterChip}
          >
            全時間帯
          </button>
          {showLunchToggle && (
            <button
              type="button"
              onClick={() => setLd('lunch')}
              className={ld === 'lunch' ? 'filter-option filter-option-active' : 'filter-option'}
              style={ld === 'lunch' ? filterChipActive : filterChip}
            >
              ランチ
            </button>
          )}
          <button
            type="button"
            onClick={() => setLd('dinner')}
            className={ld === 'dinner' ? 'filter-option filter-option-active' : 'filter-option'}
            style={ld === 'dinner' ? filterChipActive : filterChip}
          >
            ディナー
          </button>
        </div>
      </section>

      {isAdmin && ld !== 'all' && (
        <p style={{ ...muted, marginBottom: '0.75rem' }}>
          ※ {ld === 'lunch' ? 'ランチ' : 'ディナー'}モード時の F・L・家賃・固定費は、選択時間帯の売上比で按分計算しています。
        </p>
      )}

      {isAdmin ? (
        <>
      {/* Row A: FL 比率（分数表示） */}
      <FLFractionCard
        foodCost={fAmt}
        laborCost={lAmt}
        sales={sales}
        flPct={flPct}
        target={benchmark.flPct}
      />

      {/* Row B: 売上 PL 構成（当期） */}
      <section style={kpiGrid}>
        <KpiCard
          label="売上"
          value={fmtYen(sales)}
          sub={`${totalPartyCount} 組 / ${totalCustomers} 人`}
          diffPct={hasPrev ? diffPct(sales, prevSales) : null}
          diffPositiveIsGood
        />
        <PLKpiCard
          label="食材費 F"
          value={fAmt}
          pct={fPct}
          target={benchmark.foodCostPct}
          diffPct={hasPrev ? diffPct(fAmt, prevFAmt) : null}
        />
        <PLKpiCard
          label="人件費 L"
          value={lAmt}
          pct={lPct}
          target={benchmark.laborCostPct}
          diffPct={hasPrev ? diffPct(lAmt, prevLAmt) : null}
        />
        <PLKpiCard
          label="家賃"
          value={rentAmt}
          pct={rentPct}
          target={benchmark.rentPct}
          diffPct={hasPrev ? diffPct(rentAmt, prevRentAmt) : null}
        />
        <PLKpiCard
          label="その他（固定費）"
          value={otherAmt}
          pct={otherPct}
          target={benchmark.otherPct}
          diffPct={hasPrev ? diffPct(otherAmt, prevOtherAmt) : null}
        />
        <PLKpiCard
          label="利益（推定）"
          value={profitAmt}
          pct={profitPct}
          target={benchmark.profitPct}
          warningUnder
          diffPct={hasPrev ? diffPct(profitAmt, prevProfitAmt) : null}
          diffPositiveIsGood
        />
      </section>

      {/* Row B': 前期同列の参考カード（カスタム期間以外） */}
      {prevRangeInfo && (
        <>
          <p style={{ ...muted, margin: '0.25rem 0 0.5rem' }}>
            ↑ 増減％は <strong>{prevRangeInfo.label}</strong>（{formatRangeLabel(prevRangeInfo.range)}）との比較
          </p>
          <section style={kpiGridPrev}>
            <KpiCard
              label={`売上（${prevRangeInfo.label}）`}
              value={fmtYen(prevSales)}
              sub={`${prevTotalPartyCount} 組 / ${prevTotalCustomers} 人`}
              variant="prev"
            />
            <PLKpiCard
              label={`食材費 F（${prevRangeInfo.label}）`}
              value={prevFAmt}
              pct={prevFPct}
              target={benchmark.foodCostPct}
              variant="prev"
            />
            <PLKpiCard
              label={`人件費 L（${prevRangeInfo.label}）`}
              value={prevLAmt}
              pct={prevLPct}
              target={benchmark.laborCostPct}
              variant="prev"
            />
            <PLKpiCard
              label={`家賃（${prevRangeInfo.label}）`}
              value={prevRentAmt}
              pct={prevRentPct}
              target={benchmark.rentPct}
              variant="prev"
            />
            <PLKpiCard
              label={`その他（固定費）（${prevRangeInfo.label}）`}
              value={prevOtherAmt}
              pct={prevOtherPct}
              target={benchmark.otherPct}
              variant="prev"
            />
            <PLKpiCard
              label={`利益（推定）（${prevRangeInfo.label}）`}
              value={prevProfitAmt}
              pct={prevProfitPct}
              target={benchmark.profitPct}
              warningUnder
              variant="prev"
            />
          </section>
        </>
      )}

      {/* Row C: 借入（全社固定、店舗フィルター無視） */}
      <section style={cardSection}>
        <h2 style={sectionTitle}>借入（会社全体）</h2>
        <div style={{ ...kpiGrid, marginBottom: 0 }}>
          <KpiCard label="借入残高合計" value={fmtYen(loanSummary.outstanding)} />
          <KpiCard label="毎月返済額合計" value={fmtYen(loanSummary.monthly)} />
          <KpiCard
            label="完済までの最長月"
            value={loanSummary.longest > 0 ? `${loanSummary.longest} ヶ月` : '—'}
          />
        </div>
      </section>

      {/* PL グラフ */}
      <section style={cardSection}>
        <h2 style={sectionTitle}>PL（売上 vs F / L / 家賃 / 他 / 利益）</h2>
        {period === 'week' && (
          <p style={{ ...muted, marginTop: 0, marginBottom: '0.5rem' }}>
            ※ グラフは参考に直近 12 週の推移を表示（KPI は選択週のみ）
          </p>
        )}
        {points.length === 0 || points.every((p) => p.sales === 0) ? (
          <p style={muted}>データがまだありません。CSV をアップロードしてください。</p>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={points}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tickFormatter={(v) => `${Math.round(Number(v) / 10000)}万`} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'FL率') return [fmtPct(Number(value)), name];
                  return [fmtYen(Number(value)), name];
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="f" name="食材費 F" stackId="cost" fill="#d6006c" />
              <Bar yAxisId="left" dataKey="l" name="人件費 L" stackId="cost" fill="#edbb00" />
              <Bar yAxisId="left" dataKey="rent" name="家賃" stackId="cost" fill="#0088b0" />
              <Bar yAxisId="left" dataKey="other" name="その他" stackId="cost" fill="#6b6379" />
              <Bar yAxisId="left" dataKey="profit" name="利益" stackId="cost" fill="#006d8d" />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="flPct"
                name="FL率"
                stroke="#212529"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <ReferenceLine
                yAxisId="right"
                y={benchmark.flPct}
                stroke="#aa0b56"
                strokeDasharray="5 5"
                label={{ value: `目標 ${benchmark.flPct}%`, fontSize: 10, fill: '#aa0b56', position: 'right' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {points.length > 0 && points.some((p) => p.sales !== 0) && (
          <PointsTablePL points={points} />
        )}
      </section>
        </>
      ) : (
        <section style={kpiGrid}>
          <KpiCard
            label="売上"
            value={fmtYen(sales)}
            sub={`${totalPartyCount} 組 / ${totalCustomers} 人`}
            diffPct={hasPrev ? diffPct(sales, prevSales) : null}
            diffPositiveIsGood
          />
          <PLKpiCard
            label="食材費 F"
            value={fAmt}
            pct={fPct}
            target={benchmark.foodCostPct}
            diffPct={hasPrev ? diffPct(fAmt, prevFAmt) : null}
          />
        </section>
      )}

      {/* 売上グラフ */}
      <section style={cardSection}>
        <h2 style={sectionTitle}>売上（客単価 / 来客 / F・D 注文）</h2>
        {points.length === 0 || points.every((p) => p.sales === 0) ? (
          <p style={muted}>データがまだありません。CSV をアップロードしてください。</p>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={points}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tickFormatter={(v) => `${Math.round(Number(v) / 10000)}万`} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === '客単価') return [fmtYen(Number(value)), name];
                  if (name === '売上') return [fmtYen(Number(value)), name];
                  return [Number(value).toLocaleString(), name];
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="sales" name="売上" fill="#aa0b56" />
              <Line yAxisId="right" type="monotone" dataKey="customers" name="来客数" stroke="#0088b0" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="avgSpend" name="客単価" stroke="#6b6379" strokeWidth={1.5} strokeDasharray="3 3" dot={{ r: 2 }} />
              <Line yAxisId="right" type="monotone" dataKey="foodQty" name="フード注文数" stroke="#006d8d" strokeWidth={1.5} dot={{ r: 2 }} />
              <Line yAxisId="right" type="monotone" dataKey="drinkQty" name="ドリンク注文数" stroke="#edbb00" strokeWidth={1.5} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {points.length > 0 && points.some((p) => p.sales !== 0) && (
          <PointsTableSales points={points} />
        )}
      </section>

      {/* 既存セクション: 時間帯別取引（棒グラフ）・カテゴリ・決済・商品トップ・席別 */}
      {latestMonthlySummary && (
        <>
          <section style={cardSection}>
            <h2 style={sectionTitle}>時間帯別取引（{selectedYearMonth}）</h2>
            {(latestMonthlySummary.hourlyDistribution ?? []).length === 0 ? (
              <p style={muted}>—</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={latestMonthlySummary.hourlyDistribution ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `${v}時`}
                  />
                  <YAxis
                    yAxisId="left"
                    tickFormatter={(v) => `${Math.round(Number(v) / 10000)}万`}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === '売上') return [fmtYen(Number(value)), name];
                      return [Number(value).toLocaleString(), name];
                    }}
                    labelFormatter={(label) => `${label} 時台`}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="sales" name="売上" fill="#aa0b56" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="txnCount"
                    name="取引数"
                    stroke="#0088b0"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
            <p style={hint}>会計時刻ベース。棒＝売上（左軸）、線＝取引数（右軸）</p>
          </section>

          <section style={twoColGrid}>
            <div style={cardSection}>
              <h2 style={sectionTitle}>カテゴリ別売上（{selectedYearMonth}）</h2>
              {(latestMonthlySummary.byCategory ?? []).length === 0 ? (
                <p style={muted}>—</p>
              ) : (
                <CategoryDonut data={latestMonthlySummary.byCategory ?? []} />
              )}
            </div>
            <div style={cardSection}>
              <h2 style={sectionTitle}>決済方法別売上（{selectedYearMonth}）</h2>
              <PaymentDonut data={latestMonthlySummary.byPayment ?? []} />
            </div>
          </section>

          <section style={twoColGrid}>
            <div style={cardSection}>
              <h2 style={sectionTitle}>売上上位 商品（{selectedYearMonth}、トップ 10）</h2>
              {(latestMonthlySummary.topProducts ?? []).length === 0 ? (
                <p style={muted}>—</p>
              ) : (
                <ol style={{ paddingLeft: '1.2rem', margin: 0 }}>
                  {latestMonthlySummary.topProducts.slice(0, 10).map((p) => (
                    <li key={p.menuName} style={{ padding: '0.2rem 0', fontSize: '0.85rem' }}>
                      <strong>¥{p.sales.toLocaleString()}</strong> / {p.quantity}個 — {p.menuName}
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div style={cardSection}>
              <h2 style={sectionTitle}>伝票（席）別売上トップ 10（{selectedYearMonth}）</h2>
              {(latestMonthlySummary.byReceipt ?? []).length === 0 ? (
                <p style={muted}>—</p>
              ) : (
                <ol style={{ paddingLeft: '1.2rem', margin: 0 }}>
                  {latestMonthlySummary.byReceipt.slice(0, 10).map((r) => (
                    <li key={r.receiptName} style={{ padding: '0.2rem 0', fontSize: '0.85rem' }}>
                      <strong>¥{r.sales.toLocaleString()}</strong> / {r.txnCount}回 — {r.receiptName}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

// ============================================================================
// KPI カード
// ============================================================================
interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  variant?: 'normal' | 'prev';
  diffPct?: number | null;
  diffPositiveIsGood?: boolean;
}
const KpiCard = ({ label, value, sub, variant, diffPct, diffPositiveIsGood }: KpiCardProps) => {
  const isPrev = variant === 'prev';
  const cardStyle = isPrev
    ? { ...kpiCardStyle, background: '#f8f9fa', borderColor: '#e9ecef', borderStyle: 'dashed' as const }
    : kpiCardStyle;
  const valColor = isPrev ? '#868e96' : '#212529';
  return (
    <div style={cardStyle}>
      <div style={kpiLabel}>{label}</div>
      <div style={{ ...kpiValue, color: valColor }}>{value}</div>
      {sub && <div style={kpiSub}>{sub}</div>}
      {!isPrev && diffPct != null && (
        <DiffBadge diffPct={diffPct} positiveIsGood={diffPositiveIsGood ?? false} />
      )}
    </div>
  );
};

// 前期比 +/- % バッジ
//   diffPositiveIsGood=true（売上・利益）: + が緑、- が赤
//   diffPositiveIsGood=false（経費）: + が赤、- が緑
interface DiffBadgeProps {
  diffPct: number;
  positiveIsGood: boolean;
}
const DiffBadge = ({ diffPct, positiveIsGood }: DiffBadgeProps) => {
  const sign = diffPct > 0 ? '+' : '';
  const isUp = diffPct > 0;
  const isGood = isUp ? positiveIsGood : !positiveIsGood;
  const color = diffPct === 0 ? '#868e96' : isGood ? '#006d8d' : '#aa0b56';
  return (
    <div
      style={{
        marginTop: 4,
        fontSize: '0.78rem',
        fontWeight: 600,
        color,
      }}
    >
      前期比 {sign}
      {diffPct.toFixed(1)}%
    </div>
  );
};

interface FLFractionCardProps {
  foodCost: number;
  laborCost: number;
  sales: number;
  flPct: number | null;
  target: number;
}
const FLFractionCard = ({ foodCost, laborCost, sales, flPct, target }: FLFractionCardProps) => {
  const over = flPct != null && flPct > target;
  const fl = foodCost + laborCost;
  return (
    <section
      style={{
        ...cardSection,
        borderColor: over ? '#d6006c' : '#e9ecef',
        padding: '1rem 1.25rem 1.1rem',
        marginBottom: '1rem',
      }}
    >
      <div style={kpiLabel}>
        FL 率（食材費 + 人件費 ÷ 売上、目標 ≤ {target}%）
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1.25rem',
          marginTop: 10,
        }}
      >
        {/* 分数 */}
        <div style={{ minWidth: 280, flex: '1 1 320px' }}>
          <div
            style={{
              padding: '0.4rem 0',
              borderBottom: '2px solid #495057',
              textAlign: 'center',
              fontSize: '0.95rem',
            }}
          >
            食材費 <strong>{fmtYen(foodCost)}</strong>
            <span style={{ color: '#868e96', margin: '0 0.4rem' }}>＋</span>
            人件費 <strong>{fmtYen(laborCost)}</strong>
            <span style={{ color: '#868e96', margin: '0 0.4rem' }}>＝</span>
            <strong>{fmtYen(fl)}</strong>
          </div>
          <div
            style={{
              padding: '0.4rem 0',
              textAlign: 'center',
              fontSize: '0.95rem',
            }}
          >
            売上 <strong>{fmtYen(sales)}</strong>
          </div>
        </div>

        {/* イコール */}
        <div style={{ fontSize: '1.4rem', color: '#868e96', fontWeight: 600 }}>＝</div>

        {/* パーセンテージ */}
        <div style={{ minWidth: 220, flex: '1 1 240px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <span
              style={{
                fontSize: '2.6rem',
                fontWeight: 700,
                color: over ? '#aa0b56' : '#212529',
                lineHeight: 1,
              }}
            >
              {fmtPct(flPct)}
            </span>
            <span style={{ fontSize: '0.82rem', color: '#868e96' }}>
              vs 目標 ≤ {target}%
            </span>
          </div>
        </div>
      </div>

      {/* プログレスバーはカード全幅に独立配置 */}
      <div style={{ marginTop: '1rem' }}>
        <ProgressBar
          value={flPct ?? 0}
          max={Math.max(target * 2, flPct ?? 0, 1)}
          target={target}
          highlight={over ? 'red' : 'normal'}
        />
      </div>
    </section>
  );
};

interface PLKpiCardProps {
  label: string;
  value: number;
  pct: number | null;
  target: number;
  warningUnder?: boolean;
  variant?: 'normal' | 'prev';
  diffPct?: number | null;
  diffPositiveIsGood?: boolean;
}
const PLKpiCard = ({
  label,
  value,
  pct,
  target,
  warningUnder,
  variant,
  diffPct,
  diffPositiveIsGood,
}: PLKpiCardProps) => {
  const isPrev = variant === 'prev';
  // 利益 (warningUnder=true): 目標%を下回ったら赤
  // 経費 (warningUnder=false): 目標%を上回ったら赤
  const under = warningUnder && pct != null && pct < target;
  const over = !warningUnder && pct != null && pct > target;
  const isWarning = under || over;
  // 負の利益等は絶対値で max を拡張
  const barMax = Math.max(target * 2, Math.abs(pct ?? 0), 1);
  const baseStyle = isPrev
    ? { ...kpiCardStyle, background: '#f8f9fa', borderStyle: 'dashed' as const, borderColor: '#dee2e6' }
    : { ...kpiCardStyle, borderColor: isWarning ? '#d6006c' : '#e9ecef' };
  const valColor = isPrev ? '#868e96' : isWarning ? '#aa0b56' : '#212529';
  // 経費系（diffPositiveIsGood が未指定の場合）：増加=悪、減少=良
  const positiveIsGood = diffPositiveIsGood ?? (warningUnder ?? false);
  return (
    <div style={baseStyle}>
      <div style={kpiLabel}>
        {label} <span style={{ color: '#868e96' }}>（目標 {target}%）</span>
      </div>
      <div style={{ ...kpiValue, color: valColor }}>{fmtYen(value)}</div>
      <div style={kpiSub}>{fmtPct(pct)}</div>
      <ProgressBar
        value={pct ?? 0}
        max={barMax}
        target={target}
        highlight={isPrev ? 'normal' : isWarning ? 'red' : 'normal'}
      />
      {!isPrev && diffPct != null && (
        <DiffBadge diffPct={diffPct} positiveIsGood={positiveIsGood} />
      )}
    </div>
  );
};

interface ProgressBarProps {
  value: number;
  max: number;
  target: number;
  highlight: 'normal' | 'red';
}
const ProgressBar = ({ value, max, target, highlight }: ProgressBarProps) => {
  // 負値（赤字利益等）はバー幅 0、目標ラインは max に対する位置で描画
  const safeValue = Math.max(0, value);
  const pct = Math.min(100, (safeValue / max) * 100);
  const targetPct = Math.min(100, Math.max(0, (target / max) * 100));
  return (
    <div style={{ position: 'relative', height: 6, background: '#f1f3f5', borderRadius: 3, marginTop: 6 }}>
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: highlight === 'red' ? '#d6006c' : '#0088b0',
          borderRadius: 3,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: `${targetPct}%`,
          top: -2,
          bottom: -2,
          width: 2,
          background: '#495057',
        }}
      />
    </div>
  );
};

// ============================================================================
// グラフデータの内訳表
// ============================================================================
interface PointsTableProps {
  points: {
    label: string;
    sales: number;
    f: number;
    l: number;
    rent: number;
    other: number;
    profit: number;
    flPct: number | null;
    avgSpend: number | null;
    customers: number;
    partyCount: number;
    foodQty: number;
    drinkQty: number;
  }[];
}

// PL グラフの内訳表（横軸=期間、縦軸=指標）
const PointsTablePL = ({ points }: PointsTableProps) => {
  const totals = points.reduce(
    (acc, p) => ({
      sales: acc.sales + p.sales,
      f: acc.f + p.f,
      l: acc.l + p.l,
      rent: acc.rent + p.rent,
      other: acc.other + p.other,
      profit: acc.profit + p.profit,
    }),
    { sales: 0, f: 0, l: 0, rent: 0, other: 0, profit: 0 },
  );
  const totalFlPct = totals.sales > 0 ? ((totals.f + totals.l) / totals.sales) * 100 : null;
  // 行＝指標、列＝期間（最終列に合計）
  const rows: { label: string; values: (string | null)[]; total: string; isProfit?: boolean }[] = [
    {
      label: '売上',
      values: points.map((p) => fmtYen(p.sales)),
      total: fmtYen(totals.sales),
    },
    {
      label: '食材費 F',
      values: points.map((p) => fmtYen(p.f)),
      total: fmtYen(totals.f),
    },
    {
      label: '人件費 L',
      values: points.map((p) => fmtYen(p.l)),
      total: fmtYen(totals.l),
    },
    {
      label: '家賃',
      values: points.map((p) => fmtYen(p.rent)),
      total: fmtYen(totals.rent),
    },
    {
      label: 'その他',
      values: points.map((p) => fmtYen(p.other)),
      total: fmtYen(totals.other),
    },
    {
      label: '利益',
      values: points.map((p) => fmtYen(p.profit)),
      total: fmtYen(totals.profit),
      isProfit: true,
    },
    {
      label: 'FL率',
      values: points.map((p) => fmtPct(p.flPct)),
      total: fmtPct(totalFlPct),
    },
  ];
  return (
    <div style={tableScroll}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>指標</th>
            {points.map((p) => (
              <th key={p.label} style={{ ...th, textAlign: 'right' }}>
                {p.label}
              </th>
            ))}
            <th style={{ ...th, textAlign: 'right', background: '#fff5f5' }}>合計</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const profitColor = (val: string): React.CSSProperties =>
              row.isProfit && val.includes('-') ? { color: '#aa0b56' } : {};
            return (
              <tr key={ri}>
                <td style={{ ...td, fontWeight: 600 }}>{row.label}</td>
                {row.values.map((v, i) => (
                  <td
                    key={i}
                    style={{ ...td, textAlign: 'right', ...profitColor(v ?? '') }}
                  >
                    {v}
                  </td>
                ))}
                <td
                  style={{
                    ...td,
                    textAlign: 'right',
                    fontWeight: 700,
                    background: '#fff5f5',
                    ...profitColor(row.total),
                  }}
                >
                  {row.total}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// 売上グラフの内訳表（横軸=期間、縦軸=指標）
const PointsTableSales = ({ points }: PointsTableProps) => {
  const totals = points.reduce(
    (acc, p) => ({
      sales: acc.sales + p.sales,
      customers: acc.customers + p.customers,
      partyCount: acc.partyCount + p.partyCount,
      foodQty: acc.foodQty + p.foodQty,
      drinkQty: acc.drinkQty + p.drinkQty,
    }),
    { sales: 0, customers: 0, partyCount: 0, foodQty: 0, drinkQty: 0 },
  );
  const totalAvgSpend = totals.customers > 0 ? totals.sales / totals.customers : null;
  const rows: { label: string; values: string[]; total: string }[] = [
    {
      label: '売上',
      values: points.map((p) => fmtYen(p.sales)),
      total: fmtYen(totals.sales),
    },
    {
      label: '来客数',
      values: points.map((p) => p.customers.toLocaleString()),
      total: totals.customers.toLocaleString(),
    },
    {
      label: '組数',
      values: points.map((p) => p.partyCount.toLocaleString()),
      total: totals.partyCount.toLocaleString(),
    },
    {
      label: '客単価',
      values: points.map((p) => fmtYen(p.avgSpend)),
      total: fmtYen(totalAvgSpend),
    },
    {
      label: 'フード注文数',
      values: points.map((p) => p.foodQty.toLocaleString()),
      total: totals.foodQty.toLocaleString(),
    },
    {
      label: 'ドリンク注文数',
      values: points.map((p) => p.drinkQty.toLocaleString()),
      total: totals.drinkQty.toLocaleString(),
    },
  ];
  return (
    <div style={tableScroll}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>指標</th>
            {points.map((p) => (
              <th key={p.label} style={{ ...th, textAlign: 'right' }}>
                {p.label}
              </th>
            ))}
            <th style={{ ...th, textAlign: 'right', background: '#fff5f5' }}>合計</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              <td style={{ ...td, fontWeight: 600 }}>{row.label}</td>
              {row.values.map((v, i) => (
                <td key={i} style={{ ...td, textAlign: 'right' }}>
                  {v}
                </td>
              ))}
              <td
                style={{
                  ...td,
                  textAlign: 'right',
                  fontWeight: 700,
                  background: '#fff5f5',
                }}
              >
                {row.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// スタイル
// ============================================================================
const pageHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};
const pageTitle: React.CSSProperties = { margin: 0, fontSize: '1.4rem' };
const filterBar: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '0.75rem 1rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
  alignItems: 'center',
};
const filterGroup: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  flexWrap: 'wrap',
};
const filterLabel: React.CSSProperties = {
  fontSize: '0.78rem',
  color: '#868e96',
  marginRight: '0.3rem',
};
const filterChip: React.CSSProperties = {
  padding: '0.3rem 0.7rem',
  borderRadius: 999,
  border: '1px solid #dee2e6',
  background: '#fff',
  color: '#495057',
  cursor: 'pointer',
  fontSize: '0.82rem',
};
const filterChipActive: React.CSSProperties = {
  ...filterChip,
  background: '#aa0b56',
  borderColor: '#aa0b56',
  color: '#fff',
  fontWeight: 600,
};
const inputInline: React.CSSProperties = {
  padding: '0.3rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  fontSize: '0.82rem',
  background: '#fff',
};
const navBtn: React.CSSProperties = {
  padding: '0.3rem 0.55rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.78rem',
  color: '#495057',
};
const kpiGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem',
};
const kpiGridPrev: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem',
  opacity: 0.95,
};
const cardSection: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.85rem' };
const hint: React.CSSProperties = { color: '#868e96', fontSize: '0.78rem', marginTop: 4 };
const twoColGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
  gap: '1rem',
};
const kpiCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '0.9rem 1rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
};
const kpiLabel: React.CSSProperties = { color: '#6c757d', fontSize: '0.8rem' };
const kpiValue: React.CSSProperties = { fontSize: '1.4rem', fontWeight: 700, marginTop: 4 };
const kpiSub: React.CSSProperties = { fontSize: '0.78rem', color: '#868e96', marginTop: 4 };
const tableScroll: React.CSSProperties = { overflowX: 'auto', marginTop: '0.75rem' };
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.82rem',
  whiteSpace: 'nowrap',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.6rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
  fontWeight: 600,
  color: '#495057',
};
const td: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  borderBottom: '1px solid #f1f3f5',
};
